import React, { useEffect, useRef } from "react";
import "../../_css/TeacherManagement.css";
import { useState } from "react";
import {
  getAllSchools,
  getSchoolCodes,
  getPlans,
  updateTeacher,
  updateTeacherPlan,
} from "../../requests";
import { notifyError, notifySuccess } from "../helper/Toaster";
import {
  deleteAuthToken,
  isValidEmail,
  isValidPhoneNumber,
} from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import SearchableDropdown from "../common/SearchableDropdown";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";

const UpdateTeacher = ({ fn, data, readOnly }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileRef = useRef(null);
  const [schools, setSchools] = useState([]);
  const [schoolCodes, setSchoolCodes] = useState([]);
  const [plans, setPlans] = useState([]);

  // Helper functions to convert between plan labels and values
  const getPlanLabel = (planValue) => {
    if (!planValue) return "";
    const plan = plans.find((p) => p.value === planValue);
    return plan ? plan.label : "";
  };

  const getPlanValue = (planLabel) => {
    if (!planLabel) return null;
    const plan = plans.find((p) => p.label === planLabel);
    return plan ? plan.value : null;
  };

  const planLabels = plans.map((p) => p.label);

  const [teacherData, setTeacherData] = useState({
    profile: data.profile,
    name: data.name,
    email: data.email,
    phone_number: data.phone_number,
    school: data.school,
    province: data.province,
    school_level: data.school_level,
    position: data.position,
    grade: data.grade,
    subject: data.subject,
    school_code: data.school_code,
    school_location: data.location,
    user_id: data.id,
    plan_id: data.plan_id || null,
  });
  const [profile, setProfile] = useState(teacherData.profile);
  const [file, setFile] = useState(null);

  const fetchPlans = async () => {
    dispatch(startLoader());
    try {
      const response = await getPlans();
      setPlans(response.data.plans);
    } catch (error) {
      if (error?.response?.status == 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }
      notifyError(error?.response?.data?.error || "Something went wrong");
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleFileChange = (e) => {
    if (readOnly) {
      return;
    }

    setFile(e.target.files[0]);
    setProfile(URL.createObjectURL(e.target.files[0]));
  };

  const handleChange = (e) => {
    if (e.target.name == "school_code") {
      //find object from schoolCodes array
      const schoolCodeObject = schoolCodes.find(
        (el) => el.school_code == e.target.value
      );
      if (!schoolCodeObject) {
        setTeacherData((p) => ({
          ...p,
          school: "",
          school_location: "",
          school_code: e.target.value,
        }));
        return;
      }
      setTeacherData((p) => ({
        ...p,
        school: schoolCodeObject?.school_name,
        school_location: schoolCodeObject?.location,
        school_code: e.target.value,
      }));
      return;
    }
    setTeacherData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    // validations for primary fields like name, email, province, school
    if (!teacherData.name) {
      notifyError("Please enter a name");
      return false;
    }

    const nameRegex = /^[A-Za-z ]{1,30}$/;

    if (!nameRegex.test(teacherData.name)) {
      notifyError("Please enter a name that contains only letters");
      return false;
    }

    if (!teacherData.email) {
      notifyError("Please enter an email address");
      return false;
    }

    if (!isValidEmail(teacherData.email)) {
      notifyError("Please enter a valid email address");
      return false;
    }

    // if (!teacherData.phone_number) {
    //   notifyError("Please enter a phone number");
    //   return false;
    // }

    // if (!isValidPhoneNumber(teacherData.phone_number)) {
    //   notifyError("Please enter a phone number of 10 digits");
    //   return false;
    // }

    // if (!teacherData.province) {
    //   notifyError("Please select province");
    //   return false;
    // }

    // if (!teacherData.school_code ) {
    //   notifyError("Please select the school code");
    //   return false;
    // }

    return true;
  };

  const handlePlanUpdate = async () => {
    if (readOnly) {
      return;
    }
    try {
      const response = await updateTeacherPlan(teacherData);
    } catch (error) {
      if (error?.response?.status == 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }
      notifyError(error?.response?.data?.error || "Something went wrong");
    }
  };

  const handleSubmit = async () => {
    if (readOnly) {
      return;
    }
    if (!validate()) {
      return;
    }

    const formData = new FormData();
    formData.append("profile", file);
    formData.append("name", teacherData.name);
    formData.append("email", teacherData.email);
    formData.append("phone_number", teacherData.phone_number);
    formData.append("school", teacherData.school);
    formData.append("province", teacherData.province);
    formData.append("school_level", teacherData.school_level);
    formData.append("position", teacherData.position);
    formData.append("grade", teacherData.grade);
    formData.append("subject", teacherData.subject);
    formData.append("school_code", teacherData.school_code);
    formData.append("school_location", teacherData.school_location);

    dispatch(startLoader());

    try {
      const response = await updateTeacher(data.id, formData);
      // await handlePlanUpdate();

      if (response.status < 400) {
        notifySuccess(
          response?.data?.message || "Teacher Updated successfully"
        );
        fn();
      }
    } catch (error) {
      if (error?.response?.status == 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }

      notifyError(error?.response?.data?.error || "Something went wrong");
    } finally {
      dispatch(stopLoader());
    }
  };

  const getSchools = async () => {
    try {
      const schoolRes = await getAllSchools();
      const uniqueSchools = schoolRes?.data?.schools.filter(
        (e) => e != "Other"
      ); // Filter out 'Other' school
      setSchools(uniqueSchools);
    } catch (error) {
      // notifyError("Error fetching schools");
    }
  };

  const provinces = [
    // States
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",

    // Union Territories
    "Andaman and Nicobar Islands",
    "Chandigarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Jammu and Kashmir",
    "Ladakh",
    "Lakshadweep",
    "Puducherry",
  ];

  const schoolTypes = [
    "Primary School",
    "High School",
    "Secondary School",
    "Higher Secondary School",
    "International School",
  ];

  const grades = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const schoolPositions = [
    "Principal",
    "Vice Principal",
    "Head Teacher",
    "Teacher",
    "Assistant Teacher",
    "Coordinator",
    "Librarian",
    "Counselor",
    "Lab Assistant",
    "Support Staff",
    "Other",
  ];

  const subjects = [
    "Mathematics",
    "Science",
    "Physics",
    "Chemistry",
    "Biology",
    "English",
    "History",
    "Geography",
    "Computer Science",
    "Economics",
    "Political Science",
    "Environmental Studies",
    "Other",
  ];

  //school code
  const getAllSchoolCodes = async () => {
    try {
      const schoolCodesRes = await getSchoolCodes();

      if (schoolCodesRes.status == 200) {
        setSchoolCodes(schoolCodesRes?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching school codes:", error);
    }
  };

  useEffect(() => {
    getSchools();
    getAllSchoolCodes();
    fetchPlans();
  }, []);

  return (
    <div className="teacher-add-main">
      {data != null && (
        <div className="add-teacher-container">
          <div className="add-teacher-head">
            <div> {teacherData.name} </div>
            <img
              style={{ cursor: "pointer" }}
              onClick={() => fn()}
              src="/close.svg"
            />
          </div>
          <div className="add-teacher-form">
            <div className="update-teacher-img">
              <input
                type="file"
                style={{ display: "none" }}
                onChange={handleFileChange}
                ref={fileRef}
              />
              <img
                onClick={() => !readOnly && fileRef.current.click()}
                src={profile}
                alt="profile"
                width="100%"
                height="100%"
              />
            </div>
            <button
              className="my-btn"
              onClick={() => fileRef.current.click()}
              style={{ marginTop: "10px", cursor: "pointer" }}
            >
              Upload profile
            </button>

            <div className="add-teacher-input-field" style={{ width: "437px" }}>
              <label className="add-teacher-label">Enter Full name</label>
              <input
                className="add-teacher-input"
                placeholder="Name"
                name="name"
                value={teacherData.name}
                onChange={handleChange}
                readOnly={readOnly}
              />
            </div>
            <div className="di-flex" style={{ width: "437px" }}>
              <div className="add-teacher-input-field">
                <label className="add-teacher-label">Mail</label>
                <input
                  type="email"
                  className="add-teacher-input"
                  placeholder="example@domain.com"
                  name="email"
                  value={teacherData.email}
                  onChange={handleChange}
                  readOnly={readOnly}
                />
              </div>
              <div className="add-teacher-input-field">
                <label className="add-teacher-label">Phone</label>
                <input
                  type="number"
                  className="add-teacher-input no-spinner"
                  placeholder="Phone"
                  name="phone_number"
                  value={teacherData.phone_number}
                  onInput={(e) => {
                    if (e.target.value.length > 10) {
                      e.target.value = e.target.value.slice(0, 10);
                    }
                  }}
                  onChange={handleChange}
                  readOnly={readOnly}
                />
              </div>
            </div>

            <div className="di-flex" style={{ width: "437px" }}>
              <div className="add-teacher-input-field">
                <label className="add-teacher-label">School Code</label>
                <SearchableDropdown
                  options={[...schoolCodes.map((e) => e.school_code)]}
                  selected={teacherData.school_code}
                  setSelected={(e) =>
                    handleChange({ target: { name: "school_code", value: e } })
                  }
                  allowManualEntry={!readOnly}
                  showDrpDown={!readOnly}
                />
              </div>

              <div className="add-teacher-input-field">
                <label className="add-teacher-label">School</label>
                <input
                  type="text"
                  className="add-teacher-input"
                  placeholder="School"
                  name="school"
                  value={teacherData.school}
                  readOnly={true}
                />
              </div>
            </div>
            <div className="di-flex" style={{ width: "437px" }}>
              <div className="add-teacher-input-field">
                <label className="add-teacher-label">School Location</label>
                <input
                  type="text"
                  className="add-teacher-input"
                  placeholder="School Location"
                  name="school_location"
                  value={teacherData.school_location}
                  readOnly={true}
                />
              </div>

              <div className="add-teacher-input-field">
                <label className="add-teacher-label">Province</label>
                <SearchableDropdown
                  options={[...provinces]}
                  selected={teacherData.province}
                  setSelected={(e) =>
                    setTeacherData((p) => ({ ...p, province: e }))
                  }
                  allowManualEntry={!readOnly}
                  showDrpDown={!readOnly}
                />
              </div>
            </div>
            <div className="di-flex" style={{ width: "437px" }}>
              <div className="add-teacher-input-field">
                <label className="add-teacher-label">School Type</label>
                <SearchableDropdown
                  options={[...schoolTypes]}
                  selected={teacherData.school_level}
                  setSelected={(e) =>
                    setTeacherData((p) => ({ ...p, school_level: e }))
                  }
                  allowManualEntry={!readOnly}
                  showDrpDown={!readOnly}
                />
              </div>
              <div className="add-teacher-input-field">
                <label className="add-teacher-label">Position</label>
                <SearchableDropdown
                  options={[...schoolPositions]}
                  selected={teacherData.position}
                  setSelected={(e) =>
                    setTeacherData((p) => ({ ...p, position: e }))
                  }
                  allowManualEntry={!readOnly}
                  showDrpDown={!readOnly}
                />
              </div>
            </div>
            <div className="di-flex" style={{ width: "437px" }}>
              <div className="add-teacher-input-field">
                <label className="add-teacher-label">Grade</label>
                <SearchableDropdown
                  options={[...grades]}
                  selected={teacherData.grade}
                  setSelected={(e) =>
                    setTeacherData((p) => ({ ...p, grade: e }))
                  }
                  type="number"
                  allowManualEntry={false}
                  showDrpDown={!readOnly}
                />
              </div>

              <div className="add-teacher-input-field">
                <label className="add-teacher-label">Subject</label>
                <SearchableDropdown
                  options={[...subjects]}
                  selected={teacherData.subject}
                  setSelected={(e) =>
                    setTeacherData((p) => ({ ...p, subject: e }))
                  }
                  allowManualEntry={!readOnly}
                  showDrpDown={!readOnly}
                />
              </div>
            </div>
            {/* <div className="di-flex" style={{ width: "437px" }}>
              {planLabels.length > 0 && (
                <div className="add-teacher-input-field">
                  <label className="add-teacher-label">Plan Type</label>
                  <SearchableDropdown
                    options={planLabels}
                    selected={getPlanLabel(teacherData.plan_id)}
                    setSelected={(e) => {
                      const planValue = getPlanValue(e);
                      setTeacherData((p) => ({ ...p, plan_id: planValue }));
                    }}
                    allowManualEntry={!readOnly}
                    showDrpDown={!readOnly}
                  />
                </div>
              )}
            </div> */}
          </div>
          <div className="add-teacher-submit">
            {!readOnly && (
              <button onClick={handleSubmit} className="add-teacher-submit-btn">
                Save changes
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateTeacher;
